/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AngularFireDatabase, SnapshotAction } from '@angular/fire/compat/database';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { BehaviorSubject, Subject, combineLatest, Observable, Subscription, merge } from 'rxjs';
import { concatMap, filter, map, mergeMap, scan, switchMap, takeUntil, tap, takeWhile } from 'rxjs/operators';
import { TicketFields } from '@touchplan/common';
import { TicketIndexLogger } from './ticket-index-logger.service';
import { AbstractTicketIndexQuery } from './index-query/abstract-ticket-index-query';
import { ITicketIndexData } from './ticket-index-data';
import firebase from 'firebase/compat/app';

export interface INumberedTicketSnapshot {
	ticketSnapshot: SnapshotAction<TicketFields>;
	ticketChangeCount: number;
}

export interface IWindowSnapshotData {
	subscribed: boolean;
	snapshot: SnapshotAction<TicketFields>;
}

export interface IWindowTicketData {
	subscribed: boolean;
	ticketFields: TicketFields;
}

/**
 * This class can be used to:
 *
 *    - Join ticket index data with ticket data
 *    - Do so using a query that limits the "window" of index data
 *    - The resulting Observable yields SnapshotAction objects, so not only is data produced
 *         but information about addition and removals of tickets from the query's range as well
 *
 * That public Observable is the ticketSnapshotChange$ object.
 *
 * To initialize it, call the startTicketSnapshotChanges function.  It can query against all tickets in a plan,
 * or in an entire project.  In addition, it supports an optional third query parameter that can be used to
 * limit the range (or "window") of the snapshots returned.  The query must be expressed in terms of the fields
 * in the index data, which exists at either:
 *
 *     {root}/indices/ticketsTwo/{projectId}/projectWide
 *          or
 *     {root}/indices/ticketsTwo/{projectId}/planWide/{planId}
 *
 * Here's sample code:
 *
 *    this.ticketIndexQuery = this.queryFactory.newTicketIndexQuery();
 *    this.querie$ = new Subject<AbstractIndexQuery>();
 *    this.windowCleanupRequest$ = new Subject<(window: IWindowTicketData[]) => IWindowTicketData[]>();
 *
 *    // Start yer engines...
 *    this.ticketIndexQuery.startTicketSnapshotChanges(
 *        this.planState.projectId, this.planState.planId,
 *        this.querie$, this.windowCleanupRequest$
 *     );
 *     this.querie$.next(
 *         this.queryFactory.newDateCentricIndexQuery('keyStart', '2020-10-12', 2, 2));
 *
 * To stop listening for changes to a query, call:
 *
 *     this.ticketIndexQuery.stopTicketSnapshotChanges();
 *
 * This class can be used to support the kind of data-paging needed for virtually scrolling through
 * a list of ticket data.  The Observable's output is designed to be relatively easily consumed by
 * a TicketList.
 *
 * Also see the "How TicketIndexQuery Works" page:
 *
 *     https://mocasystems.atlassian.net/wiki/spaces/DI/pages/2328068126/How+TicketIndexQuery+Works
 */
export class TicketIndexQuery {
	// This is the main Observable (and its "backing" BehaviorSubject),
	// containing the TicketFields snapshot changes for the requested range of tickets;
	// see "initTicketSnapshotChange$":
	public ticketSnapshotChange$: Observable<SnapshotAction<TicketFields>>;
	// The BehaviorSubject connected to "ticketSnapshotChange$"
	private ticketSnapshotBehaviorSubject: BehaviorSubject<SnapshotAction<TicketFields>>;

	// The subscription for "ticketSnapshotChange$";
	// started by "startTicketSnapshotChanges(...)";
	// stopped by "stopTicketSnapshotChanges()".
	private rootSubscription: Subscription;

	// This maps' values contain all of the IWindowSnapshotData objects for all of the tickets in the current
	// window of tickets that have been 'child_added' but not yet 'child_removed' (even if there isn't an active
	// subscription for the ticket anymore).
	// The map is indexed by ticket ID.
	private latestWindowMap: Map<string, IWindowSnapshotData>;

	public stopSnapshot$: Subject<void>;

	constructor(private db: AngularFireDatabase, private logger: TicketIndexLogger) {
		this.ticketSnapshotBehaviorSubject = new BehaviorSubject<SnapshotAction<TicketFields>>(null);
		this.ticketSnapshotChange$ = this.ticketSnapshotBehaviorSubject.pipe(
			filter((val) => val !== null) // Exclude the null used when constructing the BehaviorSubject
		);
		this.stopSnapshot$ = new Subject<void>();

		this.latestWindowMap = new Map<string, IWindowSnapshotData>();
	}

	/**
	 * Starts the query, preparing it to look for changes to tickets and their corresponding index data
	 * in the given project ("projectId"), and in either the plan with the given "planId", or it that's null,
	 * in all plans in the project.
	 *
	 * Queries against the data in the ticket index Firebase data:
	 *
	 *     {root}/indices/ticketsTwo/{projectId}/projectWide
	 *          or
	 *     {root}/indices/ticketsTwo/{projectId}/planWide/{planId}
	 *
	 * can be sent to this object via the "applyQueryToIndexRef$" Observable given to this function.
	 * See the "SimpleIndexQuery" and "DateCentricIndexQuery" classes for examples of such queries;
	 * they can be created via the "QueryFactory" singleton.
	 *
	 * A query is "run" when it's received from the "applyQueryToIndexRef$" Observable and started.
	 * When another query is provded by the "applyQueryToIndexRef$" Observable, the previous one is stopped
	 * and the new query is started.
	 *
	 * While a query is active and being run, each ticket has two live subscriptions: for the corresponding
	 * index entry and for the corresponding ticket data entry.  When a query is stopped because a new query
	 * appears in the "applyQueryToIndexRef$" Observable, all such Observables' subscriptions are unsubscribed.
	 *
	 * Note that a query can be reused.  For example, a date-centric query can ask for 50 tickets before
	 * a given data and 50 after the same date.  That query's "after" number field can then be increased to
	 * (say) 100, and then same query object can then be enqueued onto an "applyQueryToIndexRef$" Observable,
	 * if it's a BehaviorSubject, by adding it via a call to "next".
	 *
	 * Conceptually, the "window" of active tickets are those for which the output "ticketSnapshotChange$"
	 * Observable has emitted a 'child_added' snapshot, NOT yet followed by a 'child_removed' snapshot.
	 * Such tickets are "in" the current window.
	 *
	 * Sometimes when a query is changed, because an active one is superseded by a new one provided by
	 * "applyQueryToIndexRef$", a ticket still exists in the current "window", even though the subsequent
	 * query may not match that ticket and - therefore - won't resubscribe to the ticket's index data or
	 * the ticket's ticket data.  If a user of this class wants to "clean up" such unsubscribe data, the
	 * "windowCleanupRequest$" Observable can be used.  It can produce clean-up functions provided by the
	 * user: when each is called, each cleanup function is passed data about the current window in the form
	 * of an array of IWindowTicketData objects: one for each element in the current window.  Each element
	 * includes a "subscribed" boolean which indicates whether the currently running query has index and
	 * ticket subscriptions running for that ticket.  The cleanup function can then return an array of
	 * a subset of what it was passed.  For every element in that subset, the output "ticketSnapshotChange$"
	 * will emit a 'child_removed' snapshot, which will have the effect of removing that ticket from the
	 * window.  Thus, the tickets in that subset are removed, i.e. "cleaned-up".  From the point of view
	 * of subscribers to the "ticketSnapshotChange$" Observable, it's as if those tickets were actually
	 * deleted, or moved out of the range of the active query.
	 *
	 * @param projectId The ID of the project in which to query
	 * @param planIdThe ID of the plan in which to query, or if null query in all plans of the project
	 * @param applyQueryToIndexRef$ An Observable which provides the queries to run
	 * @param windowCleanupRequest$ An Observable which provides functions that act as "window cleanup requests"
	 */
	public startTicketSnapshotChanges(
		projectId: string,
		planId: string,
		applyQueryToIndexRef$: Observable<AbstractTicketIndexQuery>,
		windowCleanupRequest$: Observable<(ticketWindow: IWindowTicketData[]) => IWindowTicketData[]>
	): void {
		// In case the user forgot...
		if (this.rootSubscription) {
			throw new Error('Previous subscription was not stopped first');
		}
		const ticketIdToIndexObservable = new Map<string, BehaviorSubject<SnapshotAction<ITicketIndexData>>>();
		const rootObservable$ = this.initSubscriptions(
			projectId,
			planId,
			applyQueryToIndexRef$,
			windowCleanupRequest$,
			ticketIdToIndexObservable
		).pipe(
			tap((snapshot) => {
				this.updateWindow(snapshot, ticketIdToIndexObservable.has(snapshot.key));
			})
		);
		this.rootSubscription = rootObservable$.subscribe(this.ticketSnapshotBehaviorSubject);
	}

	/** Stops the currently running query, and stops all future potential query processing by the object. */
	public stopTicketSnapshotChanges(): void {
		this.stopSnapshot$.next(null);
		this.rootSubscription = null;
	}

	/**
	 * See "startTicketSnapshotChanges", mostly.
	 *
	 * In addition, this function makes use of a Map passed in as "ticketIdToIndexObservable".
	 *
	 * Every input query provides a series of snapshots.  Each snapshot pertains to a particular ticket-ID, despite
	 * in being a snapshot of index data.  This function wants to combine index snapshots for tickets with SPECIFIC
	 * ticket IDs with the corresponding subscriptions to ticket data on the corresponding ticket ID.
	 *
	 * In order to accomplish that, each input index data snapshot has its ticket ID mapped to a BehaviorSubject
	 * created specifically for THAT ticket ID.  That ticket-ID/BehaviorSubject pair is stored in the "ticketIdToIndexObservable"
	 * map.
	 *
	 * Then, when the subscription for that same ticket ID is created on the ticket data under:
	 *
	 *     .../tickets/{projectId}/{planId}/{ticketId}
	 *
	 * that subscription is combined with the TICKET-ID-specific BehaviorSubject of index snapshots for the SAME
	 * ticket-ID, the latter being accessed from the Map.
	 *
	 * @param projectId The ID of the project in which to query
	 * @param planIdThe ID of the plan in which to query, or if null query in all plans of the project
	 * @param applyQueryToIndexRef$ An Observable which provides the queries to run
	 * @param windowCleanupRequest$ An Observable which provides functions that act as "window cleanup requests"
	 * @param ticketIdToIndexObservable A per-ticket-id Map of snapshots from input index queries to BehaviorSubject values
	 * @returns The Observable that processes the queries in "applyQueryToIndexRef$" and cleanup requests in "windowCleanupRequest$"
	 */
	private initSubscriptions(
		projectId: string,
		planId: string,
		applyQueryToIndexRef$: Observable<AbstractTicketIndexQuery>,
		windowCleanupRequest$: Observable<(ticketWindow: IWindowTicketData[]) => IWindowTicketData[]>,
		ticketIdToIndexObservable: Map<string, BehaviorSubject<SnapshotAction<ITicketIndexData>>>
	) {
		const joinsOfFirebaseData = applyQueryToIndexRef$.pipe(
			tap(() => {
				// Window contents from the previous query are no longer subscribed:
				for (const key of this.latestWindowMap.keys()) {
					this.updateWindowElementsSubscription(key, false);
				}
			}),
			switchMap((indexQuery: AbstractTicketIndexQuery) => {
				return indexQuery.getIndexSnapshot$(projectId, planId).pipe(
					tap((indexSnapshot) => {
						this.forwardToIndexObservableForTicket(indexSnapshot, ticketIdToIndexObservable);
					}),
					filter((indexSnapshot) => indexSnapshot.type === 'child_added'),
					// will emit (at this point) once per index. Will only emit again for the same ticket after being removed first
					// always child_added
					mergeMap((indexSnapshot) => {
						const indexData = indexSnapshot.payload.val();
						const ticketId = indexData.ticketId;
						// gets an "unfiltered" version of the index observable for the current ticket via class state
						const ticketsIndexObservable = ticketIdToIndexObservable.get(ticketId);

						// emits whenever either the index or the ticket emits
						// unless the ticket is null
						return combineLatest([
							ticketsIndexObservable,
							this.getTicketChange$(projectId, indexData).pipe(
								tap((val) => this.logger.logTicketSnapshotChange(val)),
								filter((ticketSnap) => ticketSnap.payload.exists()), //<--- GRAR - might be an issue
								scan(
									(acc: INumberedTicketSnapshot, ticketSnap) => {
										return {
											ticketSnapshot: ticketSnap,
											ticketChangeCount: acc.ticketChangeCount + 1,
										} as INumberedTicketSnapshot;
									},
									{ ticketSnapshot: null, ticketChangeCount: 0 } as INumberedTicketSnapshot
								)
							),
						]).pipe(
							map((both) => {
								const indexSnap = both[0];
								const ticketSnap = both[1].ticketSnapshot;
								const ticketSnapCount = both[1].ticketChangeCount;

								// Don't emit redundant 'child_added' snapshots:
								if (
									indexSnap.type === 'child_added' &&
									ticketSnapCount === 1 &&
									this.latestWindowMap.get(ticketId)
								) {
									// ... but do record the fact that we've re-subscribed:
									this.updateWindow(ticketSnap, true);
									return null;
								}

								//doctors, than emits
								const doctoredSnapshot = this.handleSnapshotPair(indexSnap, ticketSnap, ticketSnapCount);

								if (indexSnap.type === 'child_removed') {
									ticketIdToIndexObservable.delete(ticketId);
									this.logger.logTicketSnapshotChangeUnsubscribe(ticketId);
								}

								return doctoredSnapshot;
							}),
							filter((doctor) => !!doctor), //filter out nulls
							//stop listening when the index is removed
							takeWhile((doctor) => doctor.type !== 'child_removed', true), //<--- true makes it emit the final value, then complete
							takeUntil(this.stopSnapshot$)
						);
					}),
					takeUntil(this.stopSnapshot$)
				);
			})
		);
		const childRemovedSnapshotsForCleanup = windowCleanupRequest$.pipe(
			map((windowInspectionFunction) => {
				const result: Array<SnapshotAction<TicketFields>> = [];
				const windowElementsToRemove = windowInspectionFunction(this.getLatestWindow());
				for (const elem of windowElementsToRemove) {
					result.push({
						type: 'child_removed',
						key: elem.ticketFields.id,
						prevKey: null,
						payload: this.latestWindowMap.get(elem.ticketFields.id).snapshot.payload,
					} as SnapshotAction<TicketFields>);
				}
				return result;
			}),
			concatMap((x) => x),
			takeUntil(this.stopSnapshot$)
		);
		return merge(joinsOfFirebaseData, childRemovedSnapshotsForCleanup);
	}

	/**
	 * Returns the latest window of tickets and associated data.
	 * See "startTicketSnapshotChanges" for a description of the meaning of "latest window of tickets".
	 *
	 * @returns Returns the latest window of tickets and associated data.
	 */
	public getLatestWindow(): IWindowTicketData[] {
		const result = [];
		for (const value of this.latestWindowMap.values()) {
			result.push({
				subscribed: value.subscribed,
				ticketFields: value.snapshot.payload.val(),
			} as IWindowTicketData);
		}
		return result;
	}

	public getLatestWindowLength() {
		return this.latestWindowMap.size;
	}

	/**
	 * Subscribes to the ticket data for the ticket described by the given "projectId" and "indexData",
	 * returning the resulting Observable of SnapshotAction<TicketFields>.
	 */
	private getTicketChange$(
		projectId: string,
		indexData: ITicketIndexData
	): Observable<SnapshotAction<TicketFields>> {
		const ticketIdPath = fancyFirebaseGetters.getTicketPath(projectId, indexData.planId, indexData.ticketId);
		const ticketRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			ticketIdPath,
			projectId
		);
		this.logger.logTicketSnapshotChangeSubscribe(ticketRef);
		return this.db.object<TicketFields>(ticketRef).snapshotChanges();
	}

	private forwardToIndexObservableForTicket(
		indexSnapshot: SnapshotAction<ITicketIndexData>,
		ticketIdToIndexObservable: Map<string, BehaviorSubject<SnapshotAction<ITicketIndexData>>>
	) {
		const indexData = indexSnapshot.payload.val();
		const ticketId = indexData.ticketId;
		this.logger.logIndexStateChange(indexSnapshot);
		let ticketsIndexObservable = ticketIdToIndexObservable.get(ticketId);
		if (!ticketsIndexObservable) {
			ticketsIndexObservable = new BehaviorSubject<SnapshotAction<ITicketIndexData>>(indexSnapshot);
			ticketIdToIndexObservable.set(ticketId, ticketsIndexObservable);
		} else {
			ticketsIndexObservable.next(indexSnapshot);
		}
	}

	/**
	 * Wrapper around "doctorSnapshot" which simply calls logging functions, before and after "doctorSnapshot" is called
	 */
	private handleSnapshotPair(
		indexSnapshot: SnapshotAction<ITicketIndexData>,
		ticketSnapshot: SnapshotAction<TicketFields>,
		ticketChangeCount: number
	): SnapshotAction<TicketFields> {
		if (ticketSnapshot.payload.exists()) {
			this.logger.logDoctorSnapshotCall(indexSnapshot, ticketSnapshot, ticketChangeCount);
		} else {
			this.logger.logTicketSnapshotNoPayload(); //GRAR - don't think this will ever get called? Suppressed in combineLatest
		}
		const doctoredSnapshot = this.doctorSnapshot(indexSnapshot, ticketSnapshot, ticketChangeCount);
		if (doctoredSnapshot) {
			this.logger.logDoctoredTicketSnapshotChange(doctoredSnapshot);
		}
		return doctoredSnapshot;
	}

	/**
	 * Combines the given index and ticket snapshot data into one SnapshotAction<TicketFields> object,
	 * which is then to be sent as output of this class via the "ticketSnapshotChange$" Observable.
	 */
	private doctorSnapshot(
		indexSnapshot: SnapshotAction<ITicketIndexData>,
		ticketSnapshot: SnapshotAction<TicketFields>,
		ticketChangeCount: number
	): SnapshotAction<TicketFields> {
		if (!ticketSnapshot.payload.exists()) {
			return null;
		}

		// The **FIRST** "type"s of the index changes indicate something interesting about the change, such as
		// "child_added" or "child_moved".  We want to give the output ticket snapshot that FIRST index's type;
		// otherwise, for second and successive tickets, default to the ticket's change type:
		const resultType =
			ticketChangeCount === 1 ||
			indexSnapshot.type === 'child_removed' ||
			indexSnapshot.type === 'child_moved'
				? indexSnapshot.type
				: // After the first "pair", the index change's type is "stale"; prefer the ticket's type.
				// However, to make things look like "list-like" change reports, if it's "value", change it to "child_changed":
				ticketSnapshot.type === 'value'
				? 'child_changed'
				: ticketSnapshot.type;

		// The index snapshots include potential useful ordering information in the "key" and "prevKey" fields,
		// which are ticket IDs when the overall query is in a single plan, or 'planId,ticketId' when the query is
		// an entire project.
		// In the ticket snapshots, "prevKey" is always null and "key" is always the ticket ID.
		// So copy out the index snapshot's information, instead of the ticket snapshot's.
		//
		// HOWEVER, note that this is imperfect.  Firebase doesn't REALLY fix these links completely when a ticket
		// is moved.  It does generate an index change for the moved ticket, and that index event's "prevKey" and "key"
		// fields are correct.  **BUT** a move takes an index entry out of one place in a (conceptually) singly-linked
		// list - linked by the "prevKey" fields -- and it inserts it elsewhere.  The "prevKey" field is correct in the
		// new location, but the "prevKey" field of what USED to be the successor of the moved index entry, at the old
		// location, is NOT fixed.
		//
		// So users of this class should NOT rely on the "prevKey" field to "tell" the user - on an on-going basis -
		// what the correct sort order is...
		const resultKey = indexSnapshot.key;
		const resultPrevKey = indexSnapshot.prevKey;

		return {
			type: resultType,
			key: resultKey,
			prevKey: resultPrevKey,
			payload: ticketSnapshot.payload,
		} as SnapshotAction<TicketFields>;
	}

	private updateWindow(snapshot: SnapshotAction<TicketFields>, subscribed: boolean): void {
		const type = snapshot.type;
		const ticketId = snapshot.key;
		if (type === 'child_added' || type === 'child_changed') {
			this.latestWindowMap.set(ticketId, { subscribed, snapshot });
		} else if (type === 'child_removed') {
			if (this.latestWindowMap.has(ticketId)) {
				this.latestWindowMap.delete(ticketId);
			}
		}
		// Regardless of the type, update the subscribed status:
		this.updateWindowElementsSubscription(ticketId, subscribed);
	}

	private updateWindowElementsSubscription(ticketId: string, subscribed: boolean): void {
		// const element = this.latestWindowMap.get(ticketId);
		const element = this.latestWindowMap.get(ticketId);
		if (element) {
			element.subscribed = subscribed;
		}
	}
}
